import { ConfigurationService } from 'src/app/services/configuration/configuration.service';
import { DataAction, Payload, Persistence, StateRepository } from '@angular-ru/ngxs/decorators';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Selector, SelectorOptions, State, Store } from '@ngxs/store';
import produce from 'immer';
import { combineLatest, Observable, BehaviorSubject } from 'rxjs';
import { filter, first, map, tap } from 'rxjs/operators';
import { Directions } from 'src/app/models/telephonyv2';
import { UserService } from 'src/app/services/user/user.service';
import { NavigationHandlerService } from 'src/app/shared/navigation-handler/navigation-handler.service';
import { INavigationSubscriber } from 'src/app/shared/navigation-handler/navigation-handler.state.models';
import { Utilities } from '../utilities';

import * as moment from 'moment';

import { ClearMemberSessionAction } from '../member/member.models';
import { TelephonyFormattedQueryParams, TelephonyQueryParams, TelephonyQueryParamsStateModel } from './telephony-queryparams.state.models';

@Persistence({
    existingEngine: localStorage
})
@StateRepository()
@State<TelephonyQueryParamsStateModel>({
    name: 'telephonyQueryParamsState',
    defaults: {
    }
})
@Injectable()
export class TelephonyQueryParamsService extends NgxsDataRepository<TelephonyQueryParamsStateModel> {


    @Selector()
    static entryPointQueryParamsSelector(state: TelephonyQueryParamsStateModel) {
        return state.queryParams;
    }
    @Selector()
    static entryPointFormattedQueryParamsSelector(state: TelephonyQueryParamsStateModel) {
        return state.formattedQueryParams;
    }

    @Selector()
    static initializedSelector(state: TelephonyQueryParamsStateModel) {
        return state.initialized;
    }


    @SelectorOptions({ injectContainerState: false })
    @Selector([TelephonyQueryParamsService.entryPointQueryParams])
    static entryPointQueryParams(queryParams: TelephonyQueryParams) {
        return queryParams;
    }

    @SelectorOptions({ injectContainerState: false })
    @Selector([TelephonyQueryParamsService.entryPointFormattedQueryParamsSelector])
    static entryPointFormattedQueryParams(formattedQueryParams: TelephonyQueryParams) {
        return formattedQueryParams;
    }

    @SelectorOptions({ injectContainerState: false })
    @Selector([TelephonyQueryParamsService.entryPointFormattedQueryParamsSelector])
    static directionSelector(formattedQueryParams: TelephonyQueryParams) {
        return formattedQueryParams.direction;
    }


    get queryParamsSync(): TelephonyQueryParams {
        return this.store.selectSnapshot<TelephonyQueryParams>(TelephonyQueryParamsService.entryPointQueryParamsSelector);
    }

    get queryParams$(): Observable<TelephonyQueryParams> {
        return this.store.select<TelephonyQueryParams>(TelephonyQueryParamsService.entryPointQueryParamsSelector);
    }

    get formattedQueryParamsSync(): TelephonyFormattedQueryParams {
        return this.store.selectSnapshot<TelephonyFormattedQueryParams>(TelephonyQueryParamsService.entryPointFormattedQueryParamsSelector);
    }

    get formattedQueryParams$(): Observable<TelephonyFormattedQueryParams> {
        return this.store.select<TelephonyFormattedQueryParams>(TelephonyQueryParamsService.entryPointFormattedQueryParamsSelector);
    }

    get initializedSync(): boolean {
        return this.store.selectSnapshot<boolean>(TelephonyQueryParamsService.initializedSelector);
    }

    get initialized$(): Observable<boolean> {
        return this.store.select<boolean>(TelephonyQueryParamsService.initializedSelector);
    }

    private _telephonyParamsAwaiter$ = new BehaviorSubject<TelephonyFormattedQueryParams>(null);
    telephonyParamsAwaiter$ = this._telephonyParamsAwaiter$.asObservable().pipe(filter(_ => !!_));

    private get expiredTime$() {
        return this.formattedQueryParams$.pipe(map(params => {
            if (params?.paramsDate) {
                const now = moment(new Date());
                const elapsedMinutes = moment.duration(now.diff(moment(params?.paramsDate))).asMinutes();
                return (this.configurationService.telephonyParametersExpirationMinutes < elapsedMinutes);
            } else {
                // Backward compatibility. Force the expiration time
                return false;
            }
        }));
    }

    constructor(
        private store: Store,
        private configurationService: ConfigurationService,
        public navigationHandlerService: NavigationHandlerService
    ) {
        super();

        this.navigationHandlerService.addSubscriber({
            id: 'telephony-queryparams',
            isAsync: true,
            navigationCommandFunc: this.readTelephonyQueryParameters.bind(this),
            order: 1,
        });
    }

    async ngxsAfterBootstrap() {

        this.setInitialized(false);

        this.initializedAwaiterListener();

        await combineLatest([
            this.store.select<string>(UserService.userIdentifier),
            this.store.select<boolean>(ConfigurationService.initializedSelector)
        ]).pipe(filter(([userIdentifier, configInitialized]) => !!userIdentifier && configInitialized), first()).toPromise();

        await this.manageTelephonyParams();
    }
    initializedAwaiterListener() {
        combineLatest([this.initialized$, this.formattedQueryParams$]).pipe(
            filter(([initialized]) => initialized),
            map(([_, params]) => params), first()).subscribe(params => {
                this._telephonyParamsAwaiter$.next(params);
            });
    }

    private async clearParams() {
        await this.readTelephonyQueryParameters(null);
    }

    async readTelephonyQueryParameters(subscriber: INavigationSubscriber, route?: ActivatedRoute) {

        this.setInitialized(false);

        const queryParams = route?.snapshot.queryParams || {};

        const direction = queryParams.direction || queryParams.Direction;

        let newParams = {
            criteria: queryParams.criteria || queryParams.Criteria,
            direction: direction && direction.toUpperCase(),
            skillName: queryParams.skillid || queryParams.skillId || queryParams.skillname || queryParams.skillName,
            contactId: queryParams.contact || queryParams.contactid || queryParams.contactId,
            metaData: queryParams.meta || queryParams.metadata || queryParams.metaData,
            tenantId: queryParams.tenantid || queryParams.tenantId || queryParams.Tenant || queryParams.TenantId,

        } as TelephonyQueryParams;

        const currentParams = Utilities.cloneHard(this.queryParamsSync);
        if (currentParams) {
            delete currentParams['paramsHash'];
            delete currentParams['paramsDate'];
        }

        let formattedNewParams = await this.formatQueryParams(newParams);
        const newParamsHash = TelephonyQueryParamsService.generateParamsHash(formattedNewParams);

        const expiredTime = await this.expiredTime$.pipe(first()).toPromise();
        if (!currentParams || expiredTime ||

            // or explicit telephony parameters, different to the current ones
            (Utilities.somePropertiesSet(newParams) && newParamsHash != this.queryParamsSync.paramsHash)

        ) {
            newParams = {
                ...newParams,
                paramsHash: newParamsHash,
                paramsDate: new Date()
            };

            this.setQueryParams(newParams);

            // Sent event to clear member session

            formattedNewParams = { ...newParams, ...formattedNewParams };
            this.setFormattedQueryParams(formattedNewParams);

            await this.store.dispatch(new ClearMemberSessionAction({ keepContextProgress: false })).pipe(first()).toPromise();
        }

        if (!this.initializedSync) {
            this.setInitialized(true);
        }
    }

    private static generateParamsHash(params: TelephonyQueryParams) {
        const keys = Object.getOwnPropertyNames(params).sort();
        const paramObj = {};
        keys.forEach(key => {
            if (params[key]) {
                paramObj[key] = params[key];
            }
        });
        const stringify = JSON.stringify(paramObj);
        const result = btoa(stringify);

        return result;
    }

    private async formatQueryParams(queryParams: TelephonyQueryParams): Promise<TelephonyFormattedQueryParams> {

        const result = { ...queryParams } as TelephonyFormattedQueryParams;

        this.formatQueryParamsDirection(result);

        await this.formatQueryParamsOriginReason(result);

        return result;
    }

    private formatQueryParamsDirection(result: TelephonyFormattedQueryParams) {
        if (result.direction) {
            let directionMatch = Directions.find(directionItem => directionItem.label === result.direction);
            if (!directionMatch) {
                directionMatch = Directions.find(directionItem => directionItem.label === result.directionValue);
            }

            if (directionMatch) {
                result.direction = directionMatch.label;
                result.directionValue = directionMatch.value;
            } else {
                result.direction = undefined;
                result.directionValue = undefined;
            }
        }
    }

    private async formatQueryParamsOriginReason(result: TelephonyFormattedQueryParams) {
        let clearOrigin: boolean;
        let clearReason: boolean;
        if (result.tenantId) {

            if (result.skillName && result.direction) {
                clearOrigin = true;
                clearReason = true;
                if (result.direction === 'INBOUND') {
                    clearOrigin = false;
                } else if (result.direction === 'OUTBOUND') {
                    clearReason = false;
                }
            }
        }

        if (clearOrigin) {
            result.originId = undefined;
            result.origin = undefined;
            result.originExternalId = undefined;
        }

        if (clearReason) {
            result.reasonId = undefined;
            result.reason = undefined;
            result.reasonExternalId = undefined;
        }
    }

    @DataAction()
    private setInitialized(@Payload('initialized') initialized: boolean) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: TelephonyQueryParamsStateModel) => {
            draft.initialized = initialized;
        }));
    }

    @DataAction()
    private setQueryParams(@Payload('queryParams') queryParams: TelephonyQueryParams) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: TelephonyQueryParamsStateModel) => {
            draft.queryParams = queryParams;
        }));
    }

    @DataAction()
    private setFormattedQueryParams(@Payload('formattedQueryParams') formattedQueryParams: TelephonyFormattedQueryParams) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: TelephonyQueryParamsStateModel) => {
            draft.formattedQueryParams = formattedQueryParams;
        }));
    }

    async manageTelephonyParams() {
        const expiredTime = await this.expiredTime$.pipe(first()).toPromise();
        if (expiredTime) {
            await this.clearParams();
        }
    }

}
